import { useTranslation } from "next-i18next";
import {
	forwardRef,
	type JSX,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import * as Yup from "yup";
import { LocalizedNumberField } from "@repo/shared/components";

export type NumberTextFieldProps = {
	value: number | null | undefined;
	setValue?: (value: number | null | undefined) => void;
	label?: string;
	errors?: Record<string, boolean | object | string>;
	setErrors?: (value: Record<string, string>) => void;
	icon?: JSX.Element;
	integer?: boolean;
	minValue?: number;
	maxValue?: number;
	flat?: boolean;
	readOnly?: boolean;
	isLoaded?: boolean;
	decimalPlaces?: number;
	canNotBeValues?: string[];
	notGrey?: boolean;
	negativeAllowed?: boolean;
	allowNull?: boolean;
	nullPlaceholder?: string;
	onFocus?: () => void;
	onBlur?: () => void;
	iconBackground?: string;
};

export type NumberTextFieldHandle = {
	validate: () => Promise<Record<string, boolean | object | string>>;
};

/**
 * Legacy NumberTextField adapter.
 * This component maintains backward compatibility with the old NumberTextField interface
 * while internally using the new LocalizedNumberField component.
 */
const NumberTextField = forwardRef<NumberTextFieldHandle, NumberTextFieldProps>(
	(props, ref) => {
		const { t } = useTranslation();

		// Track the current numeric value
		const [currentValue, setCurrentValue] = useState<number | null>(null);
		const [isLoaded, setIsLoaded] = useState<boolean>(false);
		// Track the last external value to detect external changes
		const lastExternalValueRef = useRef<number | null | undefined>(undefined);

		// Build validation schema
		const buildValidationSchema = useCallback(() => {
			let schema = props.allowNull
				? Yup.number().nullable()
				: Yup.number()
						.required(t("required", { ns: "validation" }))
						.typeError(t("validation.number.invalid"));

			if (props.integer) {
				schema = schema.integer(t("validation.number.invalid"));
			}

			if (props.minValue !== undefined) {
				schema = schema.min(props.minValue, t("validation.number.min") + props.minValue);
			}

			if (props.maxValue !== undefined) {
				schema = schema.max(props.maxValue, t("validation.number.max") + props.maxValue);
			}

			if (props.canNotBeValues && props.canNotBeValues.length > 0) {
				const forbiddenNumbers = props.canNotBeValues.map(Number).filter((n) => !isNaN(n));
				schema = schema.notOneOf(forbiddenNumbers, t("validation.number.min") + props.minValue);
			}

			return Yup.object().shape({ number: schema });
		}, [props.allowNull, props.integer, props.minValue, props.maxValue, props.canNotBeValues, t]);

		const validateFields = useCallback(async (): Promise<Record<string, string>> => {
			const localErrors: Record<string, string> = {};

			try {
				const validationSchema = buildValidationSchema();
				await validationSchema.validate({ number: currentValue }, { abortEarly: false });
				props.setErrors?.(localErrors);
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}
				return localErrors;
			}
		}, [currentValue, buildValidationSchema, props.setErrors]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		// Sync with external value when isLoaded changes or external value changes
		useEffect(() => {
			if (props.isLoaded && !isLoaded) {
				// Initial load
				setIsLoaded(true);
				setCurrentValue(props.value ?? null);
				lastExternalValueRef.current = props.value;
			} else if (isLoaded) {
				// After load: check if external value changed (not from our own update)
				const externalValue = props.value ?? null;
				const lastKnown = lastExternalValueRef.current ?? null;
				if (externalValue !== lastKnown) {
					// External value changed - sync internal state
					setCurrentValue(externalValue);
					lastExternalValueRef.current = props.value;
				}
			}
		}, [props.isLoaded, props.value, isLoaded]);

		const handleValueChange = (value: number | null): void => {
			setCurrentValue(value);
			// Update the ref so we know this is our own change
			lastExternalValueRef.current = value;
			// Notify parent of the change
			props.setValue?.(value);
			// Clear error on change
			if (props.errors?.number) {
				props.setErrors?.({ ...props.errors, number: "" } as Record<string, string>);
			}
		};

		const handleBlur = (): void => {
			props.onBlur?.();
		};

		const handleFocus = (): void => {
			props.onFocus?.();
		};

		// Build styling based on legacy props
		// flat mode: no border (used in tables)
		const flatStyle = props.flat
			? {
					"& .MuiOutlinedInput-notchedOutline": {
						border: "none !important",
					},
				}
			: {};

		return (
			<LocalizedNumberField
				value={currentValue}
				onValueChange={handleValueChange}
				label={props.label}
				error={!!props.errors?.number}
				helperText={props.errors?.number as string}
				icon={props.icon}
				iconBackgroundColor={props.iconBackground}
				readOnly={props.readOnly}
				decimalScale={props.integer ? 0 : props.decimalPlaces}
				fixedDecimalScale={props.decimalPlaces !== undefined}
				allowNegative={props.negativeAllowed ?? false}
				className={props.notGrey ? "notGrey" : ""}
				additionalProps={{
					sx: { width: "100%", ...flatStyle },
					onFocus: handleFocus,
					onBlur: handleBlur,
				}}
			/>
		);
	},
);

NumberTextField.displayName = "NumberTextField";
export default NumberTextField;
